import type { TerritoriesAIApiV1CommonPaginatedSortableSearchableRequest } from "@territoriesai/types";
import { defineStore } from "pinia";
import { ref } from "vue";

export const enum SettingName {
  ACCOUNTS = "account_summary",
  PRICING = "pricing_summary",
  SCORING = "scoring_summary",
  CAPACITY = "capacity_summary",
  ROLES = "roles_summary",
  PEOPLE = "people_summary",
  TERRITORIES = "territories_summary",
  ORGANIZATION_SECTION = "organization_section",
  ORGANIZATION_VIEW = "organization_view",
  BILLING_VIEW = "billing_view"
}

export const enum SettingType {
  TOGGLE = "toggle",
  TABLE_COLUMN_FILTER = "table_filter",
  TABLE_QUERY = "table-query"
}

export type TableFilterValue = Array<string>;
export type ToggleValue = string;
export type TableQueryValue =
  TerritoriesAIApiV1CommonPaginatedSortableSearchableRequest;
export type WorkspaceSettingValue =
  | TableFilterValue
  | ToggleValue
  | TableQueryValue;

export interface WorkspaceSettings {
  [key: string]: WorkspaceSettingValue;
}

export const DEFAULT_WORKSPACE_SETTINGS: WorkspaceSettings = {
  // Column filters
  [`${SettingName.ACCOUNTS}:${SettingType.TABLE_COLUMN_FILTER}`]: [
    "Account ID",
    "Account Name",
    "lastModifiedTimestamp",
    "Account Type",
    "Industry",
    "Annual Revenue",
    "Customer Spend",
    "Number Of Employees",
    "Location",
    "score",
    "scoreRank",
    "tier",
    "tam",
    "weightedTam",
    "weightedTamRank"
  ],
  [`${SettingName.PRICING}:${SettingType.TABLE_COLUMN_FILTER}`]: [
    "name",
    "lastModifiedTimestamp",
    "offerings",
    "numberOfAccounts",
    "status",
    "tam",
    "menu"
  ],
  [`${SettingName.SCORING}:${SettingType.TABLE_COLUMN_FILTER}`]: [
    "name",
    "lastModifiedTimestamp",
    "tam",
    "includedAccounts",
    "status",
    "menu"
  ],
  [`${SettingName.CAPACITY}:${SettingType.TABLE_COLUMN_FILTER}`]: [
    "name",
    "lastModifiedTimestamp",
    "revenueGoal",
    "attainmentDate",
    "menu"
  ],
  [`${SettingName.ROLES}:${SettingType.TABLE_COLUMN_FILTER}`]: [
    "name",
    "lastModifiedTimestamp",
    "directReports",
    "indirectReports",
    "minDirectReports",
    "maxDirectReports",
    "maxIndirectReports",
    "menu"
  ],
  [`${SettingName.PEOPLE}:${SettingType.TABLE_COLUMN_FILTER}`]: [
    "name",
    "lastModifiedTimestamp",
    "department",
    "roleName",
    "managerName",
    "indirectManagers",
    "location",
    "menu"
  ],
  [`${SettingName.TERRITORIES}:${SettingType.TABLE_COLUMN_FILTER}`]: [
    "name",
    "lastModifiedTimestamp",
    "status",
    "menu"
  ],
  // Toggles
  [`${SettingName.ORGANIZATION_SECTION}:${SettingType.TOGGLE}`]: "People",
  [`${SettingName.ORGANIZATION_VIEW}:${SettingType.TOGGLE}`]: "Table",
  [`${SettingName.BILLING_VIEW}:${SettingType.TOGGLE}`]: "Overview",

  // Table queries
  [`${SettingName.ACCOUNTS}:${SettingType.TABLE_QUERY}`]: {
    pageNumber: 0,
    pageSize: 10,
    orderByFieldName: undefined,
    orderByDirection: "ASC",
    searchFieldName: "",
    searchFieldValue: ""
  }
};

export const useWorkspaceSettingsStore = defineStore(
  "workspaceSettings",
  () => {
    const settings = ref<WorkspaceSettings>({});
    const workspaceUserGroupId = ref<string>("");
    const version = ref<string>("v2");

    function getCachedVersion() {
      const cachedVersion = window.localStorage.getItem("settings:version");
      return cachedVersion ? JSON.parse(cachedVersion) : null;
    }

    function initialize(currentWorkspaceUserGroupId: string) {
      workspaceUserGroupId.value = currentWorkspaceUserGroupId;
      const cachedVersion = getCachedVersion();
      if (!cachedVersion) {
        window.localStorage.setItem(
          "settings:version",
          JSON.stringify(version.value)
        );
      }
      if (cachedVersion !== version.value) {
        //clear all keys that start with 'settings:${cachedVersion}' if version has changed.
        const keysToRemove = Object.keys(window.localStorage).filter((key) =>
          key.startsWith(`settings:${cachedVersion}`)
        );
        keysToRemove.forEach((key) => {
          window.localStorage.removeItem(key);
        });
        // For now just reset the settings object.
        // TODO: handle migration of settings for future changes.
        settings.value = {};
        // Update the version in localStorage
        window.localStorage.setItem(
          "settings:version",
          JSON.stringify(version.value)
        );
      }
    }

    function getDefaultSettingValue(
      settingName: SettingName,
      settingType: SettingType
    ): WorkspaceSettingValue | null {
      const key = `${settingName}:${settingType}`;
      if (key in DEFAULT_WORKSPACE_SETTINGS) {
        return DEFAULT_WORKSPACE_SETTINGS[key];
      }
      return null;
    }

    function loadSetting<
      T extends WorkspaceSettingValue = WorkspaceSettingValue
    >(settingName: SettingName, settingType: SettingType): T {
      const fullKey = makeKey(settingName, settingType);
      const raw = window.localStorage.getItem(fullKey);
      const parsed = raw
        ? JSON.parse(raw)
        : getDefaultSettingValue(settingName, settingType);
      settings.value[fullKey] = parsed;
      return settings.value[fullKey] as T;
    }

    function saveSetting(
      settingName: SettingName,
      settingType: SettingType,
      value: WorkspaceSettingValue
    ): void {
      const fullKey = makeKey(settingName, settingType);
      window.localStorage.setItem(fullKey, JSON.stringify(value));
      settings.value[fullKey] = value;
    }

    function removeSetting(settingName: SettingName, settingType: SettingType) {
      const fullKey = makeKey(settingName, settingType);
      window.localStorage.removeItem(fullKey);
      delete settings.value[fullKey];
    }

    function makeKey(
      settingName: SettingName,
      settingType: SettingType
    ): string {
      if (!workspaceUserGroupId.value) {
        throw new Error(
          "Workspace user group ID is not initialized. Call initialize() first."
        );
      }
      return `settings:${version.value}:${workspaceUserGroupId.value}:${settingName}:${settingType}`;
    }

    return {
      settings,
      workspaceUserGroupId,
      initialize,
      loadSetting,
      saveSetting,
      removeSetting,
      makeKey,
      getCachedVersion,
      getDefaultSettingValue
    };
  }
);
